import { TNetworkError } from 'shared/exceptions/network';

export interface ClickPoint {
  x: number;
  y: number;
  id: string;
}

export interface PollsSchema {
  pollDetails: {
    data?: TUnionPollDetails;
    loading: boolean;
    error?: TNetworkError;
  };
  surveyResults: {
    result?: TSinglePollResult[];
    stage: number;
    loading: boolean;
    error?: TNetworkError;
    isSended: boolean;
  };
  points: { [key: string]: ClickPoint[] };
  pollBook: {
    time?: string;
    loading: boolean;
    error?: TNetworkError;
  };
  pollRefuse: {
    loading: boolean;
    error?: TNetworkError;
  };
  pollsList: {
    data?: TPollsListResponse[];
    meta?: TPollsListMeta;
    loading: boolean;
    error?: TNetworkError;
    init: boolean;
  };
  pollRun: {
    data: (TUnionPollDetails & { run_poll_blocked_before?: string }) | null;
    loading: boolean;
    error?: TNetworkError;
  };
  target: {
    data: TargetAsk | null;
    isRun?: 'run' | 'poll';
    needAsk: boolean;
    loading: boolean;
    error?: TNetworkError;
    answer: boolean;
    answerLoading: boolean;
    answerError?: TNetworkError;
  };
}

export const TEST_TYPE = {
  COUPLE: 'couple',
  RANGE: 'range',
  STAR: 'star',
  QUESTION: 'question',
  CLICK: 'click',
  TOURNEY: 'tourney',
  BEST: 'best_from_many',
  MULTIPLE: 'multiple',
  TASK: 'task',
} as const;

export const IMAGE_PLACE = {
  0: 'image_id',
  1: 'second_image_id',
  2: 'third_image_id',
  3: 'fourth_image_id',
  4: 'fifth_image_id',
  5: 'sixth_image_id',
  6: 'seventh_image_id',
  7: 'eighth_image_id',
} as const;

export type TImagePlace = (typeof IMAGE_PLACE)[keyof typeof IMAGE_PLACE];

export type TPollsType = (typeof TEST_TYPE)[keyof typeof TEST_TYPE];

export const TEST_NAME: Record<TPollsType, string> = {
  [TEST_TYPE.COUPLE]: 'Тест один из двух',
  [TEST_TYPE.RANGE]: 'Приоритетный тест',
  [TEST_TYPE.STAR]: 'Звездный рейтинг',
  [TEST_TYPE.QUESTION]: 'Открытый вопрос',
  [TEST_TYPE.CLICK]: 'Клик-тест (heatmap)',
  [TEST_TYPE.TOURNEY]: 'Турнирный тест',
  [TEST_TYPE.BEST]: 'Лучший из вариантов',
  [TEST_TYPE.MULTIPLE]: 'Множественный выбор',
  [TEST_TYPE.TASK]: 'Задание',
} as const;

export type TAnswerMediaType = 'image' | 'video' | 'audio';

export interface TAnswerMedia {
  id?: number;
  type: TAnswerMediaType;
  link: string;
  compressed?: string | null;
  min?: string | null;
  duration?: number | string | null;
  audio_preview?: string | null;
}

export interface TPollImage {
  compressed: string | null;
  id: number;
  link: string;
  min: string | null;
  poll_id: number;
  isBlurred: boolean;
  isAdultOnly: boolean;
  type?: 'image' | 'audio' | 'video' | 'text';
  comment?: string | null;
  text?: string | null;
}

export interface TBasePollDetailsData {
  adult_only: boolean;
  created_at: Date;
  poll_process: boolean;
  updated_at: Date;
  media_type?: 'image' | 'audio' | 'video' | 'text';
  images_show_random?: boolean;
  booked_until?: string | null;
  booking_seconds_remaining?: number | null;
  respondent_payment?: number | null;
  moderation_status?: 'pending' | 'approved' | 'rejected' | null;
  moderation_comment?: string | null;
}

export interface TSinglePollDetails extends TBasePollDetailsData {
  id: number;
  composite_main_id: number | null;
  images: TPollImage[];
  type: TPollsType;
  question: string;
  task_comment?: string;
  task_max_minutes?: number;
  task_extra_reward?: number;
  answer_media?: TAnswerMedia | null;
  result?: TSavedPollResult | null;
  reworkBaseline?: {
    message: string;
    mediaKey: string;
  };
}

export interface TSavedPollResult {
  message?: string | null;
  answer_media?: TAnswerMedia | null;
  images?: { id: number }[] | null;
  rate?: number | null;
  click_coordinates?: { x: number; y: number; id?: string }[] | null;
  moderation_status?: 'pending' | 'approved' | 'rejected' | null;
  moderation_comment?: string | null;
}

export interface TPollAudio extends TPollImage {
  audio_preview: string;
}

export interface TSinglePollDetailsAudio extends TSinglePollDetails {
  images: TPollAudio[];
}

export type TPollVideo = TPollImage;

export interface TSinglePollDetailsVideo extends TSinglePollDetails {
  images: TPollVideo[];
}

export interface TPollText extends TPollImage {
  text: string;
}

export interface TSinglePollDetailsText extends TSinglePollDetails {
  images: TPollText[];
}

export interface TCompositePollDetails extends TBasePollDetailsData {
  composite: TSinglePollDetails[];
  composite_theme: string;
}

export type TUnionPollDetails = TSinglePollDetails | TCompositePollDetails;

export type TPollResultBase = {
  poll_id: number;
};

export interface TSinglePollResult extends TPollResultBase {
  image_id?: number;
  second_image_id?: number;
  third_image_id?: number;
  fourth_image_id?: number;
  fifth_image_id?: number;
  sixth_image_id?: number;
  seventh_image_id?: number;
  eighth_image_id?: number;
  message: string;
  rate?: number;
  image_ids?: number[];
  answer_media?: TAnswerMedia;
  click_coordinates?: {
    x: number;
    y: number;
  }[];
}

export interface TMultiPollResult extends TPollResultBase {
  composite: TSinglePollResult[];
}

export type TUnionPollResult = TSinglePollResult | TMultiPollResult;

export type TRequesPollstList = {
  limit: number;
  page: string;
  sort: string;
};

export interface TPollsListResponse extends TSinglePollDetails {
  composite_theme: string | null;
  composite_order: string;
  is_composite: boolean;
  own_respondents_pay: number | null;
  result: TPollsListResult | null;
  is_available: boolean;
}

export type TPollsListMeta = {
  current_page: number;
  last_page: number;
  limit: number;
  total: number;
};

export type TPollsListResult = {
  id: number;
  message: string | null;
  complaint: {
    ban: string | null;
    recommendation: string | null;
  };
  images: TPollImage[];
  media?: TAnswerMedia[];
  answer_media?: TAnswerMedia | null;
  moderation_status?: 'pending' | 'approved' | 'rejected' | null;
  moderation_comment?: string | null;
  like: boolean;
  rate: number | null;
  click_coordinates: {
    x: number;
    y: number;
  }[];
  created_at: Date;
  updated_at: Date;
};

export type TargetAsk = {
  question: string;
  id: number;
  options: string[];
};
